import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Select, message, Row, Col, Alert, Checkbox, Space, Tabs, Input } from 'antd';
import { SendOutlined, TeamOutlined, UserAddOutlined, SearchOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Option } = Select;

export default function EnviosMassa() {
  const [clientes, setClientes] = useState([]);
  const [contatos, setContatos] = useState([]);
  const [leads, setLeads] = useState([]);
  const [laudos, setLaudos] = useState({ vencidos: [], vencendo30: [], vencendo60: [], vencendo90: [] });
  const [templates, setTemplates] = useState([]);
  const [templatesMautic, setTemplatesMautic] = useState([]);
  const [templateMauticSelecionado, setTemplateMauticSelecionado] = useState(null);
  const [templateSelecionado, setTemplateSelecionado] = useState(null);
  const [clientesSelecionados, setClientesSelecionados] = useState([]);
  const [leadsSelecionados, setLeadsSelecionados] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [filtroLaudo, setFiltroLaudo] = useState("todos");
  const [filtroCidade, setFiltroCidade] = useState("");
  const [busca, setBusca] = useState("");
  const [filtroStatusLead, setFiltroStatusLead] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const [rClientes, rContatos, rLeads, rLaudos, rTemplates, rTemplatesMautic] = await Promise.all([
        api.get("/clientes"),
        api.get("/clientes/todos-contatos"),
        api.get("/leads"),
        api.get("/gestao/laudos"),
        api.get("/comunicacao/templates"),
        api.get("/comunicacao/mautic/templates"),
      ]);
      setClientes(rClientes.data);
      setContatos(rContatos?.data || []);
      setLeads(rLeads.data);
      setLaudos(rLaudos.data);
      setTemplates(rTemplates.data);
      setTemplatesMautic(rTemplatesMautic.data || []);
    } catch { message.error("Erro ao carregar dados"); }
    finally { setLoading(false); }
  };

  const clientesComLaudoVencido = new Set((laudos.vencidos || []).map(l => l.cliente_id));
  const clientesComLaudo30 = new Set((laudos.vencendo30 || []).map(l => l.cliente_id));
  const clientesComLaudo60 = new Set((laudos.vencendo60 || []).map(l => l.cliente_id));
  const clientesComLaudo90 = new Set((laudos.vencendo90 || []).map(l => l.cliente_id));

  const cidades = [...new Set(clientes.map(c => c.cidade_fiscal).filter(Boolean))].sort();

  let clientesFiltrados = contatos.length > 0 ? contatos : clientes;
  if (filtroLaudo === "vencidos") clientesFiltrados = clientesFiltrados.filter(c => clientesComLaudoVencido.has(c.id));
  if (filtroLaudo === "vencendo30") clientesFiltrados = clientesFiltrados.filter(c => clientesComLaudo30.has(c.id));
  if (filtroLaudo === "vencendo60") clientesFiltrados = clientesFiltrados.filter(c => clientesComLaudo60.has(c.id));
  if (filtroLaudo === "vencendo90") clientesFiltrados = clientesFiltrados.filter(c => clientesComLaudo90.has(c.id));
  if (filtroLaudo === "com_laudo") clientesFiltrados = clientesFiltrados.filter(c => clientesComLaudoVencido.has(c.id) || clientesComLaudo30.has(c.id) || clientesComLaudo60.has(c.id) || clientesComLaudo90.has(c.id));
  if (filtroCidade) clientesFiltrados = clientesFiltrados.filter(c => c.cidade_fiscal === filtroCidade);
  if (busca) clientesFiltrados = clientesFiltrados.filter(c => (c.razao_social || c.nome || "").toLowerCase().includes(busca.toLowerCase()));

  let leadsFiltrados = leads;
  if (filtroStatusLead) leadsFiltrados = leadsFiltrados.filter(l => l.status === filtroStatusLead);
  if (busca) leadsFiltrados = leadsFiltrados.filter(l => (l.nome || "").toLowerCase().includes(busca.toLowerCase()) || (l.empresa || "").toLowerCase().includes(busca.toLowerCase()));

  const enviarParaClientes = async () => {
    if (!templateMauticSelecionado) { message.warning("Selecione um template do Mautic"); return; }
    if (clientesSelecionados.length === 0) { message.warning("Selecione pelo menos um contato"); return; }
    setEnviando(true);
    try {
      const contatosSelecionados = clientesFiltrados.filter(c => clientesSelecionados.includes(c.id));
      const r = await api.post("/comunicacao/mautic/enviar", {
        email_id: templateMauticSelecionado,
        contatos: contatosSelecionados.map(c => ({ email: c.email, nome: c.nome }))
      });
      message.success("Envio iniciado pelo Mautic! " + r.data.enviados + " contatos.");
      setClientesSelecionados([]);
    } catch(e) { message.error("Erro: " + (e.response?.data?.erro || e.message)); }
    finally { setEnviando(false); }
  };

  const enviarParaLeads = async () => {
    if (!templateSelecionado) { message.warning("Selecione um template"); return; }
    if (leadsSelecionados.length === 0) { message.warning("Selecione pelo menos um lead"); return; }
    setEnviando(true);
    try {
      const r = await api.post("/comunicacao/envio-massa-leads", { template_id: templateSelecionado, leads_ids: leadsSelecionados });
      message.success(r.data.enviados + " emails enviados!");
      setLeadsSelecionados([]);
    } catch(e) { message.error("Erro: " + (e.response?.data?.erro || e.message)); }
    finally { setEnviando(false); }
  };

  const colunasClientes = [
    { title: "", width: 40, render: (_, r) => (
      <Checkbox checked={clientesSelecionados.includes(r.id)} onChange={e => {
        if (e.target.checked) setClientesSelecionados([...clientesSelecionados, r.id]);
        else setClientesSelecionados(clientesSelecionados.filter(id => id !== r.id));
      }} />
    )},
    { title: "Contato", render: (_, r) => <><div><strong>{r.nome}</strong>{r.principal && <Tag color="blue" style={{marginLeft:4}}>Principal</Tag>}</div><div style={{fontSize:11,color:"#888"}}>{r.email}</div></> },
    { title: "Cliente", render: (_, r) => r.razao_social || r.nome_cliente },
    { title: "Cidade", render: (_, r) => r.cidade ? r.cidade + "/" + r.estado : "-", width: 120 },
    { title: "Laudos", width: 200, render: (_, r) => (
      <Space size={2}>
        {clientesComLaudoVencido.has(r.cliente_id) && <Tag color="red">Vencido</Tag>}
        {clientesComLaudo30.has(r.cliente_id) && <Tag color="orange">30d</Tag>}
        {clientesComLaudo60.has(r.cliente_id) && <Tag color="gold">60d</Tag>}
        {clientesComLaudo90.has(r.cliente_id) && <Tag color="blue">90d</Tag>}
      </Space>
    )},
  ];

  const colunasLeads = [
    { title: "", width: 40, render: (_, r) => (
      <Checkbox checked={leadsSelecionados.includes(r.id)} onChange={e => {
        if (e.target.checked) setLeadsSelecionados([...leadsSelecionados, r.id]);
        else setLeadsSelecionados(leadsSelecionados.filter(id => id !== r.id));
      }} />
    )},
    { title: "Nome", dataIndex: "nome" },
    { title: "Empresa", dataIndex: "empresa" },
    { title: "Email", dataIndex: "email" },
    { title: "Status", dataIndex: "status", width: 120, render: v => <Tag>{v}</Tag> },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Envios em Massa</h2>
        <p style={{ color: "#888", margin: 0 }}>Dispare emails para clientes ou leads</p>
      </div>
      <Tabs defaultActiveKey="clientes" items={[
        { key: "clientes", label: <span><TeamOutlined /> Clientes</span>, children: (
          <Card size="small">
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={7}><Select placeholder="Template do Mautic" style={{ width: "100%" }} onChange={v => setTemplateMauticSelecionado(v)}>{templatesMautic.map(t => <Option key={t.id} value={t.id}>{t.nome}</Option>)}</Select></Col>
              <Col span={5}><Select placeholder="Filtrar laudos" style={{ width: "100%" }} allowClear onChange={v => setFiltroLaudo(v || "todos")}>
                <Option value="todos">Todos os clientes</Option>
                <Option value="com_laudo">Com laudos pendentes</Option>
                <Option value="vencidos">Laudos vencidos</Option>
                <Option value="vencendo30">Vencendo 30d</Option>
                <Option value="vencendo60">Vencendo 60d</Option>
                <Option value="vencendo90">Vencendo 90d</Option>
              </Select></Col>
              <Col span={4}><Select placeholder="Cidade" style={{ width: "100%" }} allowClear onChange={v => setFiltroCidade(v || "")}>{cidades.map(c => <Option key={c} value={c}>{c}</Option>)}</Select></Col>
              <Col span={4}><Input placeholder="Buscar..." prefix={<SearchOutlined />} value={busca} onChange={e => setBusca(e.target.value)} /></Col>
            </Row>
            <Row style={{ marginBottom: 12 }}>
              <Col span={12}><Space>
                <Button type="link" onClick={() => setClientesSelecionados(clientesFiltrados.map(c => c.id))}>Selecionar todos ({clientesFiltrados.length})</Button>
                <Button type="link" onClick={() => setClientesSelecionados([])}>Limpar</Button>
              </Space></Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Button type="primary" icon={<SendOutlined />} onClick={enviarParaClientes} loading={enviando} disabled={clientesSelecionados.length === 0}>Enviar para {clientesSelecionados.length} cliente(s)</Button>
              </Col>
            </Row>
            <Table columns={colunasClientes} dataSource={clientesFiltrados} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }} />
          </Card>
        )},
        { key: "leads", label: <span><UserAddOutlined /> Leads</span>, children: (
          <Card size="small">
            <Row gutter={16} style={{ marginBottom: 12 }}>
              <Col span={7}><Select placeholder="Template do Mautic" style={{ width: "100%" }} onChange={v => setTemplateMauticSelecionado(v)}>{templatesMautic.map(t => <Option key={t.id} value={t.id}>{t.nome}</Option>)}</Select></Col>
              <Col span={5}><Select placeholder="Status" style={{ width: "100%" }} allowClear onChange={v => setFiltroStatusLead(v || "")}>
                <Option value="novo">Novo</Option>
                <Option value="contatado">Contatado</Option>
                <Option value="interessado">Interessado</Option>
                <Option value="proposta_enviada">Proposta Enviada</Option>
                <Option value="sem_interesse">Sem Interesse</Option>
              </Select></Col>
              <Col span={4}><Input placeholder="Buscar..." prefix={<SearchOutlined />} value={busca} onChange={e => setBusca(e.target.value)} /></Col>
            </Row>
            <Row style={{ marginBottom: 12 }}>
              <Col span={12}><Space>
                <Button type="link" onClick={() => setLeadsSelecionados(leadsFiltrados.map(l => l.id))}>Selecionar todos ({leadsFiltrados.length})</Button>
                <Button type="link" onClick={() => setLeadsSelecionados([])}>Limpar</Button>
              </Space></Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <Button type="primary" icon={<SendOutlined />} onClick={enviarParaLeads} loading={enviando} disabled={leadsSelecionados.length === 0}>Enviar para {leadsSelecionados.length} lead(s)</Button>
              </Col>
            </Row>
            <Table columns={colunasLeads} dataSource={leadsFiltrados} rowKey="id" size="small" loading={loading} pagination={{ pageSize: 20 }} />
          </Card>
        )},
      ]} />
    </div>
  );
}
